import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PillChip, InfoCard } from "@/components/toggles";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatError } from "@/lib/api";
import { ArrowLeft, ArrowRight, Check, Image, Tag, FileText, Euro, Truck, Sparkles, Loader2, ScanSearch } from "lucide-react";
import RichTextView from "@/components/common/RichTextView";
import { WornPhotoUpload } from "@/components/WornPhotoUpload";
import { AnonymizeDialog } from "@/components/media/AnonymizeDialog";
import { AnonymityNotice } from "@/components/media/AnonymityNotice";
import PriceHint from "@/components/common/PriceHint";
import RichTextEditor from "@/components/common/RichTextEditor";
import AIAssistBar from "@/components/ai/AIAssistBar";
import AIReviewDialog from "@/components/ai/AIReviewDialog";
import MediaAnalyseChip from "@/components/media/MediaAnalyseChip";
import ImageCropDialog from "@/components/home/ImageCropDialog";

const MP_CATS = [
  { code: "tops", label: "Tops & Shirts" },
  { code: "dresses", label: "Kleider" },
  { code: "pants", label: "Hosen" },
  { code: "shoes", label: "Schuhe" },
  { code: "bags", label: "Taschen" },
  { code: "accessories", label: "Accessoires" },
  { code: "jackets", label: "Jacken & Mäntel" },
  { code: "sportswear", label: "Sportswear" },
];

const STX_CATS = [
  { code: "socks", label: "Socken" },
  { code: "slip", label: "Slips" },
  { code: "bras", label: "BHs" },
  { code: "stockings", label: "Strümpfe" },
  { code: "lingerie", label: "Dessous" },
  { code: "sportswear", label: "Sportswear" },
];

const CONDITIONS = [
  { code: "NEW_WITH_TAGS", label: "Neu mit Etikett" },
  { code: "NEW_WITHOUT_TAGS", label: "Neu ohne Etikett" },
  { code: "VERY_GOOD", label: "Sehr gut" },
  { code: "GOOD", label: "Gut" },
  { code: "ACCEPTABLE", label: "Akzeptabel" },
];

const STX_CONDITIONS = [
  { code: "NEU", label: "Neu / Ungetragen" },
  { code: "GEPFLEGT", label: "Gepflegt" },
  { code: "GETRAGEN_LEICHT", label: "Leicht getragen" },
  { code: "GETRAGEN_MITTEL", label: "Mittel getragen" },
  { code: "GETRAGEN_INTENSIV", label: "Intensiv getragen" },
];

// Sub-Batch BF-1 4.1: Compound-Word-Matching ohne führendes \b (matched z.B. "Stinkesocken")
// und Sub-Batch BF-1 4.5: MP fängt Sock-Titles → 'accessories' (MP hat keine 'socks'-Kategorie)
// iter80.9: Keywords → neue Marketplace-Kategorien (damen_bekleidung etc.) + Sub-Kategorien.
// Returned: `{ category, subcategory, segment }`
const CATEGORY_KEYWORDS = {
  MARKETPLACE: [
    // Bekleidung ─ Herren zuerst prüfen (spezifische Signalwörter) sonst Damen (default)
    { rx: /\b(herren|men'?s|jungs?)\b.*\b(shirt|t-shirt|tee|top|hemd|tank|polo|bluse)\b/i, category: "herren_bekleidung", subcategory: "tops_shirts", segment: "MALE" },
    { rx: /\b(shirt|t-shirt|tee|top|bluse|hemd|tank|polo)\b/i, category: "damen_bekleidung", subcategory: "tops_shirts", segment: "FEMALE" },
    { rx: /\b(kleid|dress)\b/i, category: "damen_bekleidung", subcategory: "kleider", segment: "FEMALE" },
    { rx: /\b(jeans)\b/i, category: "damen_bekleidung", subcategory: "jeans", segment: "FEMALE" },
    { rx: /\b(hose|leggings?|chino|jogger)\b/i, category: "damen_bekleidung", subcategory: "hosen_leggings", segment: "FEMALE" },
    { rx: /\b(rock|r(?:ö|oe)cke?)\b/i, category: "damen_bekleidung", subcategory: "roecke", segment: "FEMALE" },
    { rx: /\b(shorts?)\b/i, category: "damen_bekleidung", subcategory: "shorts", segment: "FEMALE" },
    { rx: /\b(pullover|pulli|strick|cardigan|hoodie|sweatshirt|sweater)\b/i, category: "damen_bekleidung", subcategory: "pullover_strick", segment: "FEMALE" },
    { rx: /\b(jacke|mantel|blazer)\b/i, category: "damen_bekleidung", subcategory: "jacken_maentel", segment: "FEMALE" },
    { rx: /\b(sportbh|sport-?bh|leggings|laufshirt|activewear|gym|yoga)\b/i, category: "damen_bekleidung", subcategory: "activewear", segment: "FEMALE" },
    { rx: /\b(bikini|badeanzug|bademode)\b/i, category: "damen_bekleidung", subcategory: "bademode", segment: "FEMALE" },
    { rx: /\b(unterw(?:ä|ae)sche|slip|panty|bh)\b/i, category: "damen_bekleidung", subcategory: "unterwaesche", segment: "FEMALE" },
    { rx: /\w*(socken?|str(?:ü|u)mpfsocken?|sneaker[\s-]?socken?)\b/i, category: "damen_bekleidung", subcategory: "socken", segment: "FEMALE" },
    // Schuhe
    { rx: /\b(sneaker|sneakers)\b/i, category: "damen_schuhe", subcategory: "sneaker", segment: "FEMALE" },
    { rx: /\b(stiefel|boots?)\b/i, category: "damen_schuhe", subcategory: "stiefel", segment: "FEMALE" },
    { rx: /\b(pumps?|heels?|absatz)\b/i, category: "damen_schuhe", subcategory: "absatz", segment: "FEMALE" },
    { rx: /\b(sandalen?)\b/i, category: "damen_schuhe", subcategory: "sandalen", segment: "FEMALE" },
    { rx: /\b(schuhe?)\b/i, category: "damen_schuhe", subcategory: "sneaker", segment: "FEMALE" },
    // Accessoires
    { rx: /\b(tasche|rucksack|bag|handtasche|clutch|crossbody)\b/i, category: "accessoires", subcategory: "taschen" },
    { rx: /\b(kette|ring|armband|halskette)\b/i, category: "accessoires", subcategory: "schmuck" },
    { rx: /\b(hut|m(?:ü|ue)tze|cap|kappe)\b/i, category: "accessoires", subcategory: "kopfbedeckungen" },
    { rx: /\b(g(?:ü|ue)rtel)\b/i, category: "accessoires", subcategory: "guertel" },
    { rx: /\b(tuch|schal|scarf)\b/i, category: "accessoires", subcategory: "schals_tuecher" },
    { rx: /\b(sonnenbrille|brille)\b/i, category: "accessoires", subcategory: "sonnenbrillen" },
    // Persönliche Gegenstände
    { rx: /\b(buch|book|roman)\b/i, category: "persoenliche_gegenstaende", subcategory: "buecher" },
  ],
  STILLTOUCHEDX: [
    // Herren-Signale zuerst
    { rx: /\b(herren|men'?s|male|man)\b.*\b(sock|socken)\b/i, category: "stx_herren_intim_socken", subcategory: "sneaker_socken", segment: "MALE" },
    { rx: /\b(herren|men'?s|male)\b.*\b(slip|boxer|jockstrap)\b/i, category: "stx_herren_intim_slips", subcategory: "boxershort", segment: "MALE" },
    { rx: /\b(boxershort|boxer|jockstrap)\b/i, category: "stx_herren_intim_slips", subcategory: "boxershort", segment: "MALE" },
    // Damen (default)
    { rx: /\w*(socken?|sneaker[\s-]?socken?)\b/i, category: "stx_damen_intim_socken", subcategory: "sneaker_socken", segment: "FEMALE" },
    { rx: /\b(strumpfhose|nylon|halterlose|kniestr(?:ü|u)mpfe?|straps)\b/i, category: "stx_damen_intim_nylon", subcategory: "strumpfhose_transparent", segment: "FEMALE" },
    { rx: /\b(slip|tanga|string|panty|panties|h(?:ö|oe)schen|boyshort)\b/i, category: "stx_damen_intim_slips", subcategory: "slip_baumwolle", segment: "FEMALE" },
    { rx: /\b(bh|b(?:ü|ue)stenhalter|bra|bralette|push[\s-]?up)\b/i, category: "stx_damen_intim_bhs", subcategory: "bh_spitze", segment: "FEMALE" },
    // Schuhe
    { rx: /\b(high[\s-]?heels?|pumps|absatz)\b/i, category: "stx_damen_schuhe", subcategory: "high_heels", segment: "FEMALE" },
    { rx: /\b(sneaker)\b/i, category: "stx_damen_schuhe", subcategory: "sneaker", segment: "FEMALE" },
    // Toys / Fluids / Media
    { rx: /\b(dildo|vibrator|plug|toy)\b/i, category: "stx_damen_toys", subcategory: "sonstiges", segment: "FEMALE" },
    { rx: /\b(speichel|urin|fluid|k(?:ö|oe)rperfl(?:ü|ue)ssig)/i, category: "stx_damen_fluids", subcategory: "sonstiges", segment: "FEMALE" },
    { rx: /\b(video|bild|photo|foto|clip|livestream)/i, category: "stx_damen_media", subcategory: "photoset", segment: "FEMALE" },
  ],
};

function suggestCategory(title, area) {
  if (!title || title.length < 3) return null;
  const rules = CATEGORY_KEYWORDS[area] || [];
  for (const r of rules) {
    if (r.rx.test(title)) return r; // gibt jetzt Objekt zurück
  }
  return null;
}

// Sub-Batch SHIP-1b: Vorschlagswerte für Paketmaße & Gewicht je Kategorie
// Verkäufer kann jeden Wert manuell überschreiben. Wird nur für leere Felder gesetzt.
// length × width × height in cm, weight in Gramm.
const PACKAGE_DEFAULTS = {
  MARKETPLACE: {
    tops:        { l: 30, w: 25, h: 3,  g: 250 },
    dresses:     { l: 35, w: 28, h: 5,  g: 400 },
    pants:       { l: 35, w: 27, h: 4,  g: 500 },
    shoes:       { l: 35, w: 25, h: 15, g: 900 },
    bags:        { l: 35, w: 30, h: 12, g: 700 },
    accessories: { l: 25, w: 20, h: 5,  g: 200 },
    jackets:     { l: 40, w: 32, h: 8,  g: 900 },
    sportswear:  { l: 32, w: 26, h: 4,  g: 350 },
  },
  STILLTOUCHEDX: {
    socks:       { l: 20, w: 15, h: 3,  g: 100 },
    slip:        { l: 20, w: 15, h: 2,  g: 80  },
    bras:        { l: 25, w: 18, h: 5,  g: 150 },
    stockings:   { l: 22, w: 16, h: 2,  g: 120 },
    lingerie:    { l: 25, w: 20, h: 4,  g: 200 },
    sportswear:  { l: 30, w: 22, h: 4,  g: 300 },
  },
};

function packageDefaultsFor(area, code) {
  return (PACKAGE_DEFAULTS[area] || {})[code] || null;
}

const STEPS = [
  { key: "title", labelKey: "listing_editor_extra.step_title_segment", fallback: "Titel & Segment", icon: Tag },
  { key: "category", label: "Kategorie", icon: FileText },
  { key: "description", label: "Beschreibung", icon: FileText },
  { key: "price", label: "Preis & Zustand", icon: Euro },
  { key: "media", label: "Medien", icon: Image },
  { key: "shipping", label: "Versand", icon: Truck },
];

export default function ListingEditorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { listingId } = useParams(); // Sub-Batch P-1: Edit-Modus via /listing/edit/:listingId
  const isEditMode = !!listingId;
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const initialArea = (searchParams.get("area") || "").toUpperCase() === "STILLTOUCHEDX" ? "STILLTOUCHEDX" : "MARKETPLACE";
  const [step, setStep] = useState(0);
  const [area, setArea] = useState(initialArea);
  const [title, setTitle] = useState("");
  const [segment, setSegment] = useState("FEMALE");
  const [category, setCategory] = useState("");
  const [categoryAutoSet, setCategoryAutoSet] = useState(false);
  // iter173 · Guard: Beim Edit-Load setzt der Load-Effect category + subCategory + size
  // in derselben Batch. Der "Reset attrs when category changes"-Effect würde sonst
  // subCategory + size unmittelbar danach wieder auf "" zurücksetzen. Der Ref hält
  // eine "just-loaded"-Flag, die die erste Reset-Runde nach dem Load überspringt.
  const suppressAttrResetRef = useRef(false);
  const [description, setDescription] = useState("");
  const [condition, setCondition] = useState("VERY_GOOD");
  const [saleMode, setSaleMode] = useState("FIXED_PRICE");
  const [price, setPrice] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [mediaUrls, setMediaUrls] = useState([]);
  const [wornPhotoUrls, setWornPhotoUrls] = useState([]);
  const [titlePhotoUrl, setTitlePhotoUrl] = useState(null);
  const [bodyContentUrls, setBodyContentUrls] = useState([]);
  const [bodyRegionsByUrl, setBodyRegionsByUrl] = useState({}); // iter172d · {url: [{part, box, gender, confidence}]}
  // Sub-Batch IMG-1: upload progress tracker (one entry per concurrent upload)
  const [uploadProgress, setUploadProgress] = useState([]); // [{id, fileName, percent, error?}]
  const [lastPickedFile, setLastPickedFile] = useState(null); // iter169.11 · State fehlte → runtime-error „lastPickedFile is not defined" in Step 5
  const [cropQueue, setCropQueue] = useState([]); // iter169.12 · FIFO für Multi-Upload
  const [activeCrop, setActiveCrop] = useState(null); // aktuell im Crop-Dialog
  // iter173e · Artikelbild-Blob, der aktuell durch die Anonymisierungs-Engine läuft
  const [anonArticleFile, setAnonArticleFile] = useState(null);
  // iter173d · Ladeoverlay während Artikelbild-Upload + serverseitiger Neutralitäts-Prüfung
  const [articleUploadPhase, setArticleUploadPhase] = useState(""); // "" | "uploading" | "analyzing"
  // iter173e · Finalen (ggf. anonymisierten) Artikelbild-Blob via bestehendem
  // /upload hochladen — der Explicit-Block für STX-Artikelbilder bleibt erhalten.
  const uploadArticleBlob = async (blob) => {
    const trackId = `${blob.name || "img"}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setUploadProgress((prev) => [...prev, { id: trackId, fileName: blob.name || "Bild", percent: 0 }]);
    setArticleUploadPhase("uploading");
    const phaseTimer = setTimeout(() => setArticleUploadPhase("analyzing"), 800);
    const formData = new FormData();
    formData.append("file", blob, blob.name || "listing.jpg");
    try {
      const { data } = await api.post(`/upload?area_scope=${area}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          const pct = evt.total ? Math.round((evt.loaded * 100) / evt.total) : 0;
          setUploadProgress((prev) => prev.map((u) => u.id === trackId ? { ...u, percent: Math.min(99, pct) } : u));
        },
      });
      const fullUrl = `/api/files/${data.path}`;
      setMediaUrls((prev) => prev.length < 6 && !prev.includes(fullUrl) ? [...prev, fullUrl] : prev);
      setUploadProgress((prev) => prev.map((u) => u.id === trackId ? { ...u, percent: 100 } : u));
      setTimeout(() => setUploadProgress((prev) => prev.filter((u) => u.id !== trackId)), 600);
    } catch (uploadErr) {
      const detail = uploadErr?.response?.data?.detail;
      if (detail?.error_code === "BODY_CONTENT_NOT_ALLOWED_MP"
          || detail?.error_code === "BODY_CONTENT_NOT_ALLOWED_STX_ITEM") {
        setError(detail.message || "Auf dem Bild wurde expliziter Inhalt erkannt.");
      } else {
        setError("Upload fehlgeschlagen. Nutze alternativ eine URL.");
      }
      setUploadProgress((prev) => prev.map((u) => u.id === trackId ? { ...u, error: true, percent: 100 } : u));
      setTimeout(() => setUploadProgress((prev) => prev.filter((u) => u.id !== trackId)), 4000);
    } finally {
      clearTimeout(phaseTimer);
      setArticleUploadPhase("");
    }
  };
  // Auto-Draft: sobald der Seller Trag-Fotos in einem NEUEN Listing hochlädt,
  // erstellen wir automatisch einen Draft, damit keine listingId-Voraussetzung
  // im Weg ist (Owner-Vorgabe 2026-07-10 Iter76).
  const [draftListingId, setDraftListingId] = useState(listingId || null);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [shippingMode, setShippingMode] = useState("DIRECT_TO_BUYER");
  // Sub-Batch SHIP-1: Direktversand-Konfiguration (Spec §29/§36)
  const [carrierCode, setCarrierCode] = useState("DHL");
  const [pkgLength, setPkgLength] = useState("");
  const [pkgWidth, setPkgWidth] = useState("");
  const [pkgHeight, setPkgHeight] = useState("");
  const [pkgWeightG, setPkgWeightG] = useState("");
  const [shippingCostMode, setShippingCostMode] = useState("CUSTOM_AMOUNT");
  const [customShippingAmount, setCustomShippingAmount] = useState("");
  // Sub-Batch Q-1b: Quantity + per-listing processing delay
  const [quantity, setQuantity] = useState(1);
  const [processingDelay, setProcessingDelay] = useState(0);
  const [error, setError] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiReview, setAiReview] = useState(null); // iter171 · EU-AI-Act Freigabe
  const [loadingExisting, setLoadingExisting] = useState(isEditMode);
  const [existingStatus, setExistingStatus] = useState(null);
  // Sub-Batch TPL-1: Templates
  const [templates, setTemplates] = useState([]);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  // iter80.8 Batch 6 Sub-b: MP Katalog-State
  const [mpCatalog, setMpCatalog] = useState(null); // { tree:[], attributes:{...} } aus /marketplace/catalog/*
  const [subCategory, setSubCategory] = useState("");
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [material, setMaterial] = useState("");
  // iter88.3 Batch 5: STX Katalog + Add-Ons
  const [stxCatalog, setStxCatalog] = useState(null);   // { tree:[] }
  const [stxAddonsList, setStxAddonsList] = useState([]); // [ { code, label, public_text, gender } ]
  // iter88.9: STX-Add-On-Offers (Code + Preis) + Extra-Tragetage-Offers (Days + Preis)
  const [stxAddonOffers, setStxAddonOffers] = useState([]); // [{code, price}]
  const [stxExtraWearOffers, setStxExtraWearOffers] = useState([]); // [{days, price}]

  // Sub-Batch W-1 5b: STX-Geschlechts-Auto basierend auf User-Profil-Feld `sex`
  const userSex = user?.sex || user?.profile_sex || null;
  const stxSegmentLocked = area === "STILLTOUCHEDX" && (userSex === "MALE" || userSex === "FEMALE");

  // iter198c · STX-Steckbrief-Guard vor dem Wizard-Öffnen.
  // Ohne komplettes Facet-Profil (gender/age_range/height_cm/weight_kg/body_type)
  // darf der STX-Wizard nicht bedient werden. Backend enforced beim Publish 422,
  // aber der Frontend-Guard verhindert, dass der Verkäufer den ganzen Wizard
  // erst durchläuft und dann scheitert.
  const [stxProfileState, setStxProfileState] = useState({ loaded: false, complete: false, missing: [] });
  // STX-Identitäts-Guard (Owner 2026-06): STX-Artikel brauchen ein angelegtes STX-Profil.
  const [stxIdentityState, setStxIdentityState] = useState({ loaded: false, exists: false });
  const stxWizardBlocked = area === "STILLTOUCHEDX" && (
    (stxProfileState.loaded && !stxProfileState.complete) ||
    (stxIdentityState.loaded && !stxIdentityState.exists)
  );
  useEffect(() => {
    if (area !== "STILLTOUCHEDX") { setStxIdentityState({ loaded: true, exists: true }); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/stx/profile");
        if (!cancelled) setStxIdentityState({ loaded: true, exists: !!(data?.exists && data?.profile?.stx_handle) });
      } catch {
        if (!cancelled) setStxIdentityState({ loaded: true, exists: false });
      }
    })();
    return () => { cancelled = true; };
  }, [area]);
  useEffect(() => {
    if (area !== "STILLTOUCHEDX") { setStxProfileState({ loaded: true, complete: true, missing: [] }); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/profile/facets");
        if (cancelled) return;
        const required = ["gender", "age_range", "height_cm", "weight_kg", "body_type"];
        const missing = required.filter((k) => !data?.[k]);
        setStxProfileState({ loaded: true, complete: missing.length === 0, missing });
      } catch {
        if (!cancelled) setStxProfileState({ loaded: true, complete: false, missing: ["gender", "age_range", "height_cm", "weight_kg", "body_type"] });
      }
    })();
    return () => { cancelled = true; };
  }, [area]);

  useEffect(() => {
    // Sub-Batch P1-AUTH-GUARD: wait for AuthContext to resolve before redirecting
    if (authLoading) return;
    if (user === false) navigate("/login");
  }, [user, authLoading, navigate]);

  // Sub-Batch TPL-1: Load templates for current area when in create-mode
  useEffect(() => {
    if (isEditMode) return;
    (async () => {
      try {
        const { data } = await api.get(`/seller/listing-templates?area_scope=${area}`);
        setTemplates(data.templates || []);
      } catch {
        setTemplates([]);
      } finally {
        setTemplatesLoaded(true);
      }
    })();
  }, [area, isEditMode]);

  const applyTemplate = (tpl) => {
    if (tpl.title) setTitle(tpl.title);
    if (tpl.description) setDescription(tpl.description);
    if (tpl.category_code) { setCategory(tpl.category_code); setCategoryAutoSet(false); }
    if (tpl.condition_code) setCondition(tpl.condition_code);
    if (tpl.sale_mode) setSaleMode(tpl.sale_mode);
    if (typeof tpl.price === "number") setPrice(String(tpl.price));
    if (tpl.segment) setSegment(tpl.segment);
    if (Array.isArray(tpl.media_urls) && tpl.media_urls[0]) {
      setMediaUrl(tpl.media_urls[0]);
      setMediaUrls(tpl.media_urls.slice(0, 6));
    }
    if (tpl.shipping_mode) setShippingMode(tpl.shipping_mode);
    if (tpl.carrier_code) setCarrierCode(tpl.carrier_code);
    if (tpl.package_length_cm != null) setPkgLength(String(tpl.package_length_cm));
    if (tpl.package_width_cm != null) setPkgWidth(String(tpl.package_width_cm));
    if (tpl.package_height_cm != null) setPkgHeight(String(tpl.package_height_cm));
    if (tpl.package_weight_g != null) setPkgWeightG(String(tpl.package_weight_g));
    if (tpl.shipping_cost_mode) setShippingCostMode(tpl.shipping_cost_mode);
    if (tpl.custom_shipping_amount != null) setCustomShippingAmount(String(tpl.custom_shipping_amount));
    if (typeof tpl.quantity === "number") setQuantity(tpl.quantity);
    if (typeof tpl.processing_delay_days === "number") setProcessingDelay(tpl.processing_delay_days);
    setShowTemplatesModal(false);
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm(t("listing_editor_extra.confirm_delete_template"))) return;
    try {
      await api.delete(`/seller/listing-templates/${templateId}`);
      setTemplates((prev) => prev.filter((t) => t.template_id !== templateId));
    } catch { /* ignore */ }
  };

  // Sub-Batch P-1: Load existing listing for edit mode
  useEffect(() => {
    if (!isEditMode) return;
    (async () => {
      try {
        const { data } = await api.get(`/listings/${listingId}/edit`);
        setArea(data.area_scope || "MARKETPLACE");
        setTitle(data.title || "");
        setSegment(data.segment || "FEMALE");
        // iter173 · vor dem Setzen von category den Reset-Guard aktivieren, damit der
        // "reset attrs when category changes"-Effect NICHT die frisch geladenen
        // subCategory + size wieder auf "" zurücksetzt.
        suppressAttrResetRef.current = true;
        setCategory(data.category_code || data.category || "");
        setCategoryAutoSet(false);
        // iter80.8 Batch 6 Sub-b: strukturierte Attribute
        setSubCategory(data.subcategory_code || "");
        setSize(data.size || "");
        setColor(data.color || "");
        setMaterial(data.material || "");
        setStxAddonOffers(Array.isArray(data.stx_addon_offers) ? data.stx_addon_offers : []);
        setStxExtraWearOffers(Array.isArray(data.stx_extra_wear_offers) ? data.stx_extra_wear_offers : []);
        setDescription(data.description || "");
        setCondition(data.condition_code || data.condition || "VERY_GOOD");
        setSaleMode(data.sale_mode || "FIXED_PRICE");
        setPrice(String(data.price || ""));
        setMediaUrl((data.media_urls || [])[0] || "");
        setMediaUrls(Array.isArray(data.media_urls) ? data.media_urls.slice(0, 6) : []);
        setWornPhotoUrls(Array.isArray(data.worn_photo_urls) ? data.worn_photo_urls : []);
        setTitlePhotoUrl(data.title_photo_url || null);
        setBodyContentUrls(Array.isArray(data.body_content_urls) ? data.body_content_urls : []);
        setShippingMode(data.shipping_mode || "DIRECT_TO_BUYER");
        setCarrierCode(data.carrier_code || "DHL");
        setPkgLength(data.package_length_cm != null ? String(data.package_length_cm) : "");
        setPkgWidth(data.package_width_cm != null ? String(data.package_width_cm) : "");
        setPkgHeight(data.package_height_cm != null ? String(data.package_height_cm) : "");
        setPkgWeightG(data.package_weight_g != null ? String(data.package_weight_g) : "");
        setShippingCostMode(data.shipping_cost_mode || "CUSTOM_AMOUNT");
        setCustomShippingAmount(data.custom_shipping_amount != null ? String(data.custom_shipping_amount) : "");
        setQuantity(typeof data.quantity === "number" ? data.quantity : 1);
        setProcessingDelay(typeof data.processing_delay_days === "number" ? data.processing_delay_days : 0);
        setExistingStatus(data.listing_status);
      } catch (e) {
        setError(formatError(e));
      } finally {
        setLoadingExisting(false);
      }
    })();
  }, [isEditMode, listingId]);

  // Auto-Lock STX-Segment auf User-Geschlecht
  useEffect(() => {
    if (stxSegmentLocked && userSex) {
      setSegment(userSex);
    }
  }, [stxSegmentLocked, userSex, area]);

  // Auto-Reset Kategorie bei Wechsel des Bereichs (NICHT im Edit-Mode)
  useEffect(() => {
    if (isEditMode) return;
    setCategory("");
    setCategoryAutoSet(false);
  }, [area, isEditMode]);

  // Sub-Batch SHIP-1b: Paket-Vorschlag bei Kategorie-Wechsel; nur leere Felder vorbefüllen,
  // Verkäufer-eigene Eingaben werden nicht überschrieben.
  useEffect(() => {
    if (!category) return;
    const def = packageDefaultsFor(area, category);
    if (!def) return;
    setPkgLength((prev) => (prev && prev !== "" ? prev : String(def.l)));
    setPkgWidth((prev) => (prev && prev !== "" ? prev : String(def.w)));
    setPkgHeight((prev) => (prev && prev !== "" ? prev : String(def.h)));
    setPkgWeightG((prev) => (prev && prev !== "" ? prev : String(def.g)));
  }, [category, area]);

  // Sub-Batch W-1 5a: Auto-Kategorie-Vorschlag bei Title-Eingabe (NICHT im Edit-Mode)
  useEffect(() => {
    if (isEditMode) return;
    if (!title || title.length < 3) return;
    if (category && !categoryAutoSet) return;
    const suggested = suggestCategory(title, area);
    if (suggested) {
      // iter80.9: suggestion object with {category, subcategory, segment}
      const cat = typeof suggested === "string" ? suggested : suggested.category;
      setCategory(cat);
      if (typeof suggested === "object" && suggested.subcategory) setSubCategory(suggested.subcategory);
      // iter88.5: segment MUSS aktualisiert werden, sonst filtert der STX-Kategoriebaum
      // die gerade auto-erkannte Kategorie wieder raus (gender-Mismatch)
      if (typeof suggested === "object" && suggested.segment) setSegment(suggested.segment);
      setCategoryAutoSet(true);
    }
  }, [title, area, category, categoryAutoSet, isEditMode, segment]);

  // iter80.8 Batch 6 Sub-b: MP-Katalog beim ersten Mount laden.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: catData }, { data: attrData }] = await Promise.all([
          api.get("/marketplace/catalog/categories"),
          api.get("/marketplace/catalog/attributes"),
        ]);
        if (cancelled) return;
        setMpCatalog({ tree: catData.tree || [], attributes: attrData || {} });
      } catch (e) {
        // Fallback: bleibt null → legacy MP_CATS wird genutzt
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // iter88.3 Batch 5 Sub-a/b: STX-Katalog + Add-Ons laden (nur wenn STX aktiv)
  useEffect(() => {
    if (area !== "STILLTOUCHEDX") return;
    let cancelled = false;
    (async () => {
      try {
        const genderParam = segment === "MALE" ? "M" : segment === "FEMALE" ? "F" : "";
        const [{ data: catData }, { data: addonData }] = await Promise.all([
          api.get("/stilltouchedx/catalog/categories"),
          api.get(`/stilltouchedx/catalog/addons${genderParam ? `?gender=${genderParam}` : ""}`),
        ]);
        if (cancelled) return;
        setStxCatalog({ tree: catData.tree || [] });
        setStxAddonsList(addonData.addons || []);
      } catch (e) { /* Fallback: legacy STX_CATS */ }
    })();
    return () => { cancelled = true; };
  }, [area, segment]);

  // Dynamische Katalog-Nutzung nur für MARKETPLACE. STX ab iter88.3 (Batch 5).
  const useDynamicMpCatalog = area === "MARKETPLACE" && !!mpCatalog;
  const useDynamicStxCatalog = area === "STILLTOUCHEDX" && !!stxCatalog;
  const cats = useDynamicMpCatalog
    ? mpCatalog.tree.map((n) => ({ code: n.category_code, label: n.label,
                                    _node: n }))
    : useDynamicStxCatalog
      ? stxCatalog.tree
          .filter((n) => !segment || n.gender === (segment === "MALE" ? "M" : "F") || n.category_code === category)
          .map((n) => ({ code: n.category_code, label: n.label, _node: n }))
      : (area === "MARKETPLACE" ? MP_CATS : STX_CATS);
  const currentMpNode = useDynamicMpCatalog
    ? mpCatalog.tree.find((n) => n.category_code === category)
    : null;
  const currentStxNode = useDynamicStxCatalog
    ? stxCatalog.tree.find((n) => n.category_code === category)
    : null;
  const conditionList = useDynamicMpCatalog
    ? (mpCatalog.attributes.conditions || []).map((c) => ({ code: c.code, label: c.label, restricted: c.restricted }))
    : (area === "MARKETPLACE" ? CONDITIONS : STX_CONDITIONS);
  const sizeOptions = useDynamicMpCatalog && mpCatalog.attributes.sizes[category]
    ? mpCatalog.attributes.sizes[category].options : [];
  const colorOptions = useDynamicMpCatalog ? (mpCatalog.attributes.colors || []) : [];
  const materialOptions = useDynamicMpCatalog ? (mpCatalog.attributes.materials || []) : [];

  // Reset attrs wenn Kategorie wechselt — außer der Auto-Detect hat gerade gesetzt
  useEffect(() => {
    if (categoryAutoSet) return;
    // iter173 · Direkt nach dem Edit-Load NICHT resetten (siehe Load-Effect).
    if (suppressAttrResetRef.current) {
      suppressAttrResetRef.current = false;
      return;
    }
    setSubCategory(""); setSize("");
  }, [category, categoryAutoSet]);

  // CLEAN_REQUIRED-Regeln
  const cleanRequired = useDynamicMpCatalog && condition === "CLEAN_REQUIRED";
  const cleanRestrictions = cleanRequired
    ? (mpCatalog.attributes.conditions || []).find((c) => c.code === "CLEAN_REQUIRED")?.restrictions
    : null;

  const canProceed = () => {
    switch (step) {
      case 0: return title.length >= 3 && segment;
      case 1: return !!category && (!useDynamicMpCatalog || !!subCategory) && (!useDynamicStxCatalog || !!subCategory);
      case 2: return (description || "").replace(/<[^>]+>/g, "").trim().length >= 3;
      case 3: return parseFloat(price) > 0;
      case 4: return (mediaUrls.length > 0 || wornPhotoUrls.length > 0) && uploadProgress.filter((u) => !u.error).length === 0;
      case 5: {
        if (!shippingMode) return false;
        if (shippingMode === "DIRECT_TO_BUYER") {
          if (!carrierCode) return false;
          if (!(parseFloat(pkgLength) > 0) || !(parseFloat(pkgWidth) > 0) || !(parseFloat(pkgHeight) > 0)) return false;
          if (!(parseInt(pkgWeightG, 10) > 0)) return false;
          if (!shippingCostMode) return false;
          if (shippingCostMode === "CUSTOM_AMOUNT" && !(parseFloat(customShippingAmount) > 0)) return false;
        }
        return true;
      }
      default: return false;
    }
  };

  const handlePublish = async () => {
    setError("");
    setPublishing(true);
    // Sub-Batch IMG-1: combine multi-uploads with optional URL-input (legacy field) – dedup, max 6
    const combined = [...mediaUrls];
    if (mediaUrl && !combined.includes(mediaUrl)) combined.push(mediaUrl);
    const finalMedia = combined.slice(0, 6);
    // Sub-Batch SHIP-1: Direct shipping payload fields
    const directShipFields = shippingMode === "DIRECT_TO_BUYER" ? {
      carrier_code: carrierCode,
      package_length_cm: parseFloat(pkgLength),
      package_width_cm: parseFloat(pkgWidth),
      package_height_cm: parseFloat(pkgHeight),
      package_weight_g: parseInt(pkgWeightG, 10),
      shipping_cost_mode: shippingCostMode,
      custom_shipping_amount: shippingCostMode === "CUSTOM_AMOUNT" ? parseFloat(customShippingAmount) : undefined,
      selected_rate_id: shippingCostMode === "LIVE_RATE" ? "STANDARD" : undefined,
    } : {};
    try {
      // Owner-Vorgabe 2026-07-10 Iter76: Falls beim Media-Step schon ein
      // Draft-Listing (wegen Trag-Foto) angelegt wurde, wechseln wir auf
      // PATCH statt neuen POST — sonst hätten wir doppelte Listings.
      if (isEditMode || draftListingId) {
        const targetId = isEditMode ? listingId : draftListingId;
        await api.patch(`/listings/${targetId}`, {
          title,
          description,
          price: parseFloat(price),
          media_urls: finalMedia,
          title_photo_url: titlePhotoUrl || null,
          body_content_urls: bodyContentUrls.filter((u) => finalMedia.includes(u)),
          // iter172d · Per-Region Blur-Boxes (nur für STX relevant)
          body_regions_by_url: Object.fromEntries(
            Object.entries(bodyRegionsByUrl).filter(([u]) => finalMedia.includes(u))
          ),
          // iter173b · Kategorie/Sub-Kategorie/Segment/Attribute mit-patchen — sonst
          // gehen Änderungen im Wizard bei Edit verloren (User-Report 2026-02-28).
          category_code: category,
          subcategory_code: subCategory || undefined,
          segment,
          size: size || undefined,
          color: color || undefined,
          material: material || undefined,
          condition_code: condition,
          sale_mode: saleMode,
          condition: area === "MARKETPLACE" ? condition : undefined,
          stx_addon_offers: area === "STILLTOUCHEDX" ? stxAddonOffers.filter((o) => o.code && Number(o.price) >= 0) : undefined,
          stx_extra_wear_offers: area === "STILLTOUCHEDX" ? stxExtraWearOffers.filter((o) => o.days >= 1 && o.days <= 7 && Number(o.price) >= 0) : undefined,
          shipping_mode: shippingMode,
          quantity: Math.max(0, parseInt(quantity, 10) || 0),
          processing_delay_days: Math.max(0, parseInt(processingDelay, 10) || 0),
          listing_status: "PUBLISHED",
          ...directShipFields,
        });
        navigate(isEditMode ? "/studio/listings" : (area === "MARKETPLACE" ? `/marketplace/${targetId}` : `/stilltouchedx/${targetId}`));
      } else {
        const { data } = await api.post("/listings", {
          area_scope: area,
          title,
          description,
          category_code: category,
          subcategory_code: subCategory || undefined,
          condition_code: condition,
          sale_mode: saleMode,
          price: parseFloat(price),
          segment,
          media_urls: finalMedia,
          title_photo_url: titlePhotoUrl || null,
          body_content_urls: bodyContentUrls.filter((u) => finalMedia.includes(u)),
          // iter172d · Per-Region Blur-Boxes (nur für STX relevant)
          body_regions_by_url: Object.fromEntries(
            Object.entries(bodyRegionsByUrl).filter(([u]) => finalMedia.includes(u))
          ),
          shipping_mode: shippingMode,
          quantity: Math.max(1, parseInt(quantity, 10) || 1),
          processing_delay_days: Math.max(0, parseInt(processingDelay, 10) || 0),
          // iter80.8 Batch 6 Sub-b: strukturierte MP-Attribute
          size: size || undefined,
          color: color || undefined,
          material: material || undefined,
          // iter88.9 Batch 5-fix: STX Add-Ons als kostenpflichtige Extras + Extra-Tragetage
          stx_addon_offers: area === "STILLTOUCHEDX" ? stxAddonOffers.filter((o) => o.code && Number(o.price) >= 0) : undefined,
          stx_extra_wear_offers: area === "STILLTOUCHEDX" ? stxExtraWearOffers.filter((o) => o.days >= 1 && o.days <= 7 && Number(o.price) >= 0) : undefined,
          ...directShipFields,
        });
        // Sub-Batch TPL-1: optionally save as template
        if (saveAsTemplate && templateName.trim()) {
          try {
            await api.post("/seller/listing-templates", {
              name: templateName.trim(),
              area_scope: area,
              title, description,
              category_code: category, condition_code: condition,
              sale_mode: saleMode,
              price: parseFloat(price), segment,
              media_urls: finalMedia,
              shipping_mode: shippingMode,
              quantity: Math.max(1, parseInt(quantity, 10) || 1),
              processing_delay_days: Math.max(0, parseInt(processingDelay, 10) || 0),
              ...directShipFields,
            });
          } catch { /* ignore – listing already published */ }
        }
        // Rücksprung zum Beitrag-Entwurf (aus dem Composer heraus gestartet).
        if (searchParams.get("return") === "composer") {
          const composerArea = searchParams.get("composer_area") === "STILLTOUCHEDX" ? "STILLTOUCHEDX" : "SOCIAL";
          try {
            sessionStorage.setItem("feed_post_draft_restore", "1");
            sessionStorage.setItem("feed_post_draft_area", composerArea);
            if (data.listing_id) sessionStorage.setItem("feed_post_pending_attach", data.listing_id);
          } catch { /* ignore */ }
          navigate(composerArea === "STILLTOUCHEDX" ? "/stilltouchedx" : "/");
          setTimeout(() => {
            try { window.dispatchEvent(new CustomEvent("open-feed-composer", { detail: { mode: "post", area: composerArea } })); } catch { /* ignore */ }
          }, 200);
          return;
        }
        navigate(area === "MARKETPLACE" ? `/marketplace/${data.listing_id}` : `/stilltouchedx/${data.listing_id}`);
      }
    } catch (err) {
      setError(formatError(err));
    } finally {
      setPublishing(false);
    }
  };

  if (loadingExisting) {
    return (
      <div className="min-h-full bg-white flex items-center justify-center py-16">
        <div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="listing-editor" className="min-h-full bg-white">
      {/* Header */}
      <div className="sticky top-0 z-40 flex items-center gap-3 px-4 py-3 bg-white border-b border-[#E5E5E5]">
        <button data-testid="editor-back" onClick={() => step > 0 ? setStep(step - 1) : navigate(-1)} className="text-[#0A0A0A]">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-base font-bold text-[#0A0A0A] flex-1">
          {isEditMode ? t("listing_editor_extra.title_edit") : t("listing_editor_extra.title_new")}
          {isEditMode && existingStatus && (
            <span className="ml-2 text-[10px] font-normal text-[#999]">({existingStatus})</span>
          )}
        </h1>
        <span className="text-xs text-[#999]">{step + 1} / {STEPS.length}</span>
      </div>

      {/* Progress */}
      <div className="flex gap-1 px-4 py-2">
        {STEPS.map((s, i) => (
          <div key={s.key} className={`flex-1 h-1 transition-colors ${i <= step ? "bg-[#0A0A0A]" : "bg-[#E5E5E5]"}`} />
        ))}
      </div>

      {/* Step Labels */}
      <div className="px-4 py-2 border-b border-[#E5E5E5]">
        <p className="text-sm font-medium text-[#0A0A0A]">{STEPS[step].labelKey ? t(STEPS[step].labelKey, STEPS[step].fallback) : STEPS[step].label}</p>
      </div>

      {error && <InfoCard variant="danger" size="sm" testId="listing-editor-error">{error}</InfoCard>}

      {/* STX-Identitäts-Guard: ohne angelegtes STX-Profil kein STX-Artikel (Owner 2026-06). */}
      {area === "STILLTOUCHEDX" && stxIdentityState.loaded && !stxIdentityState.exists && (
        <div className="mx-4 mt-4 mb-2 p-4 border border-amber-300 bg-amber-50 rounded"
             data-testid="stx-identity-guard-blocker">
          <p className="text-sm font-semibold text-amber-900 mb-1.5">stilltouchedX-Profil erforderlich</p>
          <p className="text-xs text-amber-900 leading-relaxed">
            stilltouchedX-Artikel laufen unter deiner separaten stilltouchedX-Identität. Bitte lege zuerst dein
            stilltouchedX-Profil (Anzeigename & Username) an, dann kannst du Artikel in diesem Bereich erstellen.
          </p>
          <button
            data-testid="stx-identity-guard-cta"
            onClick={() => navigate("/profile/edit")}
            className="mt-3 h-9 px-4 rounded-full bg-[#0A0A0A] text-white text-xs font-medium"
          >
            stilltouchedX-Profil anlegen →
          </button>
        </div>
      )}

      {/* iter198c · STX-Steckbrief-Guard: warnt sichtbar oberhalb des Wizards.
           Publish/Save-Buttons sind dann disabled (`stxWizardBlocked`).
           iter198d · Alle Texte lokalisiert (Owner-Vorgabe „nichts hardcoded"). */}
      {area === "STILLTOUCHEDX" && stxProfileState.loaded && !stxProfileState.complete && (
        <div className="mx-4 mt-4 mb-2 p-4 border border-amber-300 bg-amber-50 rounded"
             data-testid="stx-profile-guard-blocker">
          <p className="text-sm font-semibold text-amber-900 mb-1.5">
            {t("stx_wizard_guard.title", { defaultValue: "stilltouchedX-Steckbrief unvollständig" })}
          </p>
          <p className="text-xs text-amber-900 leading-relaxed">
            {t("stx_wizard_guard.body", { defaultValue: "Bevor du STX-Artikel einstellen kannst, musst du deinen Steckbrief einmalig ausfüllen (Produktverkauf Damen/Herren, Altersbereich, Größe, Gewicht, Statur). Nur so können Käufer deine Artikel richtig einordnen." })}
          </p>
          {stxProfileState.missing.length > 0 && (
            <p className="text-[11px] text-amber-800 mt-2">
              {t("stx_wizard_guard.missing_prefix", { defaultValue: "Fehlt noch:" })} {" "}
              <b>{stxProfileState.missing.map((k) => t(`facets.${k}`, { defaultValue: k })).join(", ")}</b>
            </p>
          )}
          <button
            data-testid="stx-profile-guard-cta"
            onClick={() => navigate("/profile/facets")}
            className="mt-3 h-9 px-4 rounded-full bg-[#0A0A0A] text-white text-xs font-medium"
          >
            {t("stx_wizard_guard.cta", { defaultValue: "Zum STX-Steckbrief" })}
          </button>
        </div>
      )}

      {/* Step Content */}
      <div className="px-4 py-6 pb-32">
        {/* Step 0: Title & Segment */}
        {step === 0 && (
          <div className="space-y-5">
            {/* Sub-Batch TPL-1: Vorlage laden */}
            {!isEditMode && templates.length > 0 && (
              <InfoCard variant="info">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold text-blue-900">Du hast {templates.length} Vorlage(n) für {area === "STILLTOUCHEDX" ? "stilltouchedX" : "Marketplace"}</p>
                    <p className="text-[10px] text-blue-700 mt-0.5">{t("listing_editor_extra.quick_start")}</p>
                  </div>
                  <button
                    type="button"
                    data-testid="editor-load-template-btn"
                    onClick={() => setShowTemplatesModal(true)}
                    className="h-9 px-3 bg-blue-600 text-white text-xs font-medium hover:bg-blue-700"
                  >
                    Vorlage laden
                  </button>
                </div>
              </InfoCard>
            )}
            <div>
              <label className="text-xs font-medium text-[#666] uppercase tracking-wider">Bereich</label>
              <div className="flex gap-2 mt-2">
                {[{ v: "MARKETPLACE", l: "Marketplace" }, { v: "STILLTOUCHEDX", l: "stilltouchedX" }].map(({ v, l }) => (
                  <button key={v} data-testid={`editor-area-${v.toLowerCase()}`} onClick={() => { setArea(v); setCategory(""); }}
                    className={`flex-1 h-10 text-sm font-medium rounded-full border transition-colors ${area === v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#666] border-[#E5E5E5]"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-[#666] uppercase tracking-wider">Artikelbezeichnung *</label>
              <input data-testid="editor-title" value={title} onChange={(e) => setTitle(e.target.value)}
                className="mt-1 w-full h-12 px-4 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
                placeholder="z.B. Vintage Seidenbluse" required />
            </div>
            <div>
              <label className="text-xs font-medium text-[#666] uppercase tracking-wider">Segment *</label>
              <div className="flex gap-2 mt-2">
                {[{ v: "FEMALE", l: "Damen" }, { v: "MALE", l: "Herren" }].map(({ v, l }) => (
                  <button key={v} data-testid={`editor-segment-${v.toLowerCase()}`}
                    onClick={() => !stxSegmentLocked && setSegment(v)}
                    disabled={stxSegmentLocked && segment !== v}
                    className={`flex-1 h-10 text-sm font-medium rounded-full border transition-colors ${
                      segment === v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" :
                      stxSegmentLocked ? "bg-[#F5F5F5] text-[#CCC] border-[#E5E5E5] cursor-not-allowed" :
                      "bg-white text-[#666] border-[#E5E5E5]"
                    }`}>
                    {l}
                  </button>
                ))}
              </div>
              {stxSegmentLocked && (
                <p className="text-[10px] text-[#666] mt-2 italic">
                  Im stilltouchedX-Bereich wird das Segment automatisch deinem Profil-Geschlecht zugeordnet.
                </p>
              )}
              {area === "STILLTOUCHEDX" && !stxSegmentLocked && (
                <p className="text-[10px] text-amber-700 mt-2">
                  ⚠ Hinweis: Bei stilltouchedX wird das Segment normalerweise automatisch deinem Profil-Geschlecht zugeordnet. Vervollständige dein Profil, um diese Auto-Logik zu aktivieren.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Step 1: Category */}
        {step === 1 && (
          <div className="space-y-2">
            <p className="text-xs text-[#999] mb-1">{t("listing_editor_extra.choose_category")}</p>
            {categoryAutoSet && category && (
              <p className="text-[10px] text-green-700 mb-2 italic">
                ✓ Automatisch erkannt basierend auf deinem Titel &bdquo;{title}&ldquo;. Du kannst die Auswahl jederzeit ändern.
              </p>
            )}
            {cats.map((cat) => (
              <button key={cat.code} data-testid={`editor-cat-${cat.code}`}
                onClick={() => { setCategory(cat.code); setCategoryAutoSet(false); }}
                className={`w-full flex items-center justify-between px-4 py-3 border transition-colors ${category === cat.code ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#0A0A0A] border-[#E5E5E5] hover:border-[#999]"}`}>
                <span className="text-sm">{cat.label}</span>
                {category === cat.code && <Check size={16} />}
              </button>
            ))}

            {/* iter80.8 Sub-b: Unter-Kategorie-Picker (MP dynamisch) */}
            {useDynamicMpCatalog && currentMpNode && (
              <div className="mt-4">
                <label className="text-xs font-medium text-[#666] uppercase tracking-wider mb-2 block">
                  Unter-Kategorie *
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {currentMpNode.subcategories.map((s) => (
                    <PillChip key={s.code} testId={`editor-subcat-${s.code}`}
                      active={subCategory === s.code}
                      onClick={() => setSubCategory(s.code)}
                      variant="neutral" size="md" className="!justify-center">
                      {s.label}
                    </PillChip>
                  ))}
                </div>
              </div>
            )}

            {/* iter88.3 Batch 5 Sub-b: STX Unter-Kategorie (Add-Ons wurden in Step 3 „Preis & Zustand" verschoben — iter88.9) */}
            {useDynamicStxCatalog && currentStxNode && (
              <div className="mt-4">
                <label className="text-xs font-medium text-[#666] uppercase tracking-wider mb-2 block">
                  Unter-Kategorie *
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {currentStxNode.subcategories.map((s) => (
                    <PillChip key={s.code} testId={`editor-stx-subcat-${s.code}`}
                      active={subCategory === s.code}
                      onClick={() => setSubCategory(s.code)}
                      variant="neutral" size="md" className="!justify-center">
                      {s.label}
                    </PillChip>
                  ))}
                </div>
                <p className="text-[10px] text-[#999] mt-2">
                  💡 Kostenpflichtige Zusatz-Optionen (Add-Ons + Extra-Tragetage) definierst du in Schritt &bdquo;Preis &amp; Zustand&ldquo;.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Description (Sub-Batch W-2) */}
        {step === 2 && (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-[#666] uppercase tracking-wider">Beschreibung *</label>
              <p className="text-[10px] text-[#999] mt-0.5">
                Nutze die Toolbar für Formatierung — Fett, Kursiv, Listen und Links.
              </p>

              <div className="mt-2 relative">
                <div data-testid="editor-description">
                  <RichTextEditor
                    value={description}
                    onChange={(html) => setDescription(html)}
                    placeholder={area === "STILLTOUCHEDX"
                      ? "Beschreibe deinen Artikel sinnlich und detailliert – die KI kann dir helfen."
                      : "Beschreibe deinen Artikel hochwertig und detailliert – die KI kann dir helfen."}
                    minHeight="240px"
                    testId="editor-description-rt"
                  />
                </div>
                <AIAssistBar
                  area={area === "STILLTOUCHEDX" ? "STX" : "MARKETPLACE"}
                  value={description}
                  onApply={setDescription}
                  fieldType="description"
                  testIdSuffix="listing-desc"
                />
              </div>
              <p className="text-right text-[10px] text-[#CCC] mt-1">
                {(() => {
                  const plain = (description || "").replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ");
                  return `${plain.length}/4000`;
                })()}
              </p>
            </div>

            <p className="text-[10px] text-[#999] -mt-2">
              Du kannst frei selbst schreiben — der KI-Text-Generator unten ist optional.
            </p>

            <button type="button" data-testid="editor-ai-describe"
              onClick={async () => {
                if (!title) { setError(t("listing_editor_extra.enter_title_first")); return; }
                setError("");
                setAiGenerating(true);
                try {
                  const { data } = await api.post("/studio/helper/listing-description", {
                    title, category, area, segment,
                    additional_hints: description.slice(0, 500),
                  });
                  // iter171 · EU-AI-Act: Review-Dialog vor Übernahme
                  setAiReview({ text: data.description || "", action: area === "STILLTOUCHEDX" ? "Sinnlicher Text (STX)" : "Verkaufstext (Marketplace)" });
                } catch (e) {
                  setError(e?.response?.data?.detail || "KI-Generierung fehlgeschlagen");
                } finally { setAiGenerating(false); }
              }}
              disabled={aiGenerating || !title}
              className="w-full h-10 border border-[#0A0A0A] text-[#0A0A0A] text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-[#F5F5F5] disabled:opacity-50 transition-colors">
              <Sparkles size={14} />
              {aiGenerating
                ? "Generiere…"
                : area === "STILLTOUCHEDX"
                  ? "Sinnlichen Text generieren (STX)"
                  : "Verkaufstext generieren (Marketplace)"
              }
            </button>

            {description && (
              <details className="mt-2 border border-[#E5E5E5] p-3" open>
                <summary className="text-[10px] text-[#666] cursor-pointer font-bold uppercase tracking-wider">{t("listing_editor_extra.preview_as_buyer")}</summary>
                <RichTextView html={description} className="mt-3 text-sm text-[#0A0A0A] prose prose-sm max-w-none" />
              </details>
            )}
          </div>
        )}

        {/* Step 3: Price & Condition */}
        {step === 3 && (
          <div className="space-y-5">
            {/* Sub-Batch W-1 5c: Zustand nur im Marketplace – im STX sind alle Artikel getragen */}
            {area === "MARKETPLACE" ? (
              <div>
                <label className="text-xs font-medium text-[#666] uppercase tracking-wider">Zustand</label>
                <div className="space-y-1 mt-2">
                  {conditionList.map((c) => (
                    <button key={c.code} data-testid={`editor-cond-${c.code}`} onClick={() => setCondition(c.code)}
                      className={`w-full flex items-center justify-between px-3 py-2 text-sm border transition-colors ${condition === c.code ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#666] border-[#E5E5E5]"}`}>
                      {c.label}
                      {condition === c.code && <Check size={14} />}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-3 bg-[#FAFAFA] border border-[#E5E5E5]">
                <p className="text-xs text-[#666] leading-relaxed">
                  <strong>Hinweis:</strong> Im stilltouchedX-Bereich sind alle Artikel grundsätzlich getragen.
                  Der genaue Zustand wird nicht separat erfasst – die Details werden über deine Artikelbeschreibung kommuniziert.
                </p>
              </div>
            )}

            {/* iter80.9: Globale MP-Regel — Erotik/Body-Bilder sind im gesamten MP verboten,
                nicht nur bei CLEAN_REQUIRED. */}
            {area === "MARKETPLACE" && (
              <div data-testid="editor-mp-global-notice" className="p-3 bg-[#FAFAFA] border border-[#E5E5E5]">
                <p className="text-[11px] text-[#666] leading-snug">
                  ℹ Bilder mit erotischem Inhalt sind im Marketplace nicht erlaubt.
                </p>
              </div>
            )}

            {/* iter80.8 Batch 6 Sub-b: CLEAN_REQUIRED-Warnhinweise */}
            {cleanRequired && cleanRestrictions && (
              <InfoCard variant="warning" testId="editor-clean-required-notice" className="space-y-1.5">
                <p className="text-xs font-bold text-amber-900">⚠ Pflichthinweise – Zustand &bdquo;Reinigung notwendig&ldquo;</p>
                {(cleanRestrictions.mandatory_notices || []).map((note, i) => (
                  <p key={i} className="text-[11px] text-amber-900 leading-snug">• {note}</p>
                ))}
              </InfoCard>
            )}

            {/* iter80.8 Batch 6 Sub-b: Dynamische Attribut-Felder (MP) */}
            {useDynamicMpCatalog && (
              <div data-testid="editor-attributes-panel" className="space-y-3 border-t border-[#E5E5E5] pt-4">
                <p className="text-xs font-medium text-[#0A0A0A]">Attribute</p>

                {sizeOptions.length > 0 && (
                  <div>
                    <label className="text-[10px] font-medium text-[#666] uppercase tracking-wider">{t("listing_editor_extra.size")}</label>
                    <select data-testid="editor-attr-size" value={size} onChange={(e) => setSize(e.target.value)}
                      className="w-full h-10 mt-1 px-3 border border-[#E5E5E5] text-sm bg-white">
                      <option value="">{t("listing_editor_extra.please_choose")}</option>
                      {sizeOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                )}

                {colorOptions.length > 0 && (
                  <div>
                    <label className="text-[10px] font-medium text-[#666] uppercase tracking-wider">Farbe</label>
                    <select data-testid="editor-attr-color" value={color} onChange={(e) => setColor(e.target.value)}
                      className="w-full h-10 mt-1 px-3 border border-[#E5E5E5] text-sm bg-white">
                      <option value="">– keine Angabe –</option>
                      {colorOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                )}

                {materialOptions.length > 0 && (
                  <div>
                    <label className="text-[10px] font-medium text-[#666] uppercase tracking-wider">Material</label>
                    <select data-testid="editor-attr-material" value={material} onChange={(e) => setMaterial(e.target.value)}
                      className="w-full h-10 mt-1 px-3 border border-[#E5E5E5] text-sm bg-white">
                      <option value="">– keine Angabe –</option>
                      {materialOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                )}
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-[#666] uppercase tracking-wider">Verkaufstyp</label>
              <div className="flex gap-2 mt-2">
                <button onClick={() => setSaleMode("FIXED_PRICE")} className={`flex-1 h-10 text-sm font-medium border transition-colors ${saleMode === "FIXED_PRICE" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#666] border-[#E5E5E5]"}`}>Festpreis</button>
                {/* iter79: Auction ist Backlog (Modul M Phase 2). Deaktiviert bis Feature scharfgeschaltet. */}
                <button
                  type="button"
                  disabled
                  title={t("listing_editor_extra.coming_soon")}
                  data-testid="editor-sale-mode-auction-disabled"
                  className="flex-1 h-10 text-sm font-medium border bg-white text-[#CCC] border-[#E5E5E5] cursor-not-allowed relative"
                >
                  Auktion
                  <span className="absolute -top-1.5 -right-1.5 text-[9px] bg-[#F5F5F5] text-[#999] px-1.5 py-0.5 border border-[#E5E5E5] rounded-full">Bald</span>
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-[#666] uppercase tracking-wider">{t("listing_editor_extra.price_eur")}</label>
              <input data-testid="editor-price" type="number" step="0.01" min="1" value={price} onChange={(e) => setPrice(e.target.value)}
                className="mt-1 w-full h-12 px-4 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
                placeholder="0.00" required />
              <PriceHint
                endpoint="listing"
                params={{ area, category, subcategory: subCategory, condition, size }}
                onApply={(m) => setPrice(String(m))}
                testid="editor-price-hint"
              />
            </div>
            {/* Sub-Batch Q-1b: Quantity (Anzahl verfügbar) */}
            <div>
              <label className="text-xs font-medium text-[#666] uppercase tracking-wider">Anzahl *</label>
              <input data-testid="editor-quantity" type="number" min="0" max="999" step="1" value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="mt-1 w-full h-12 px-4 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
                placeholder="1" />
              <p className="mt-1 text-[11px] text-[#999] leading-relaxed">
                Wie viele Stück verkaufst du? Bei <strong>0</strong> {t("listing_editor_extra.auto_switch_pre")} <em>{t("listing_editor_extra.auto_switch_post")}</em> gesetzt.
                Bei jedem Kauf wird der Wert um 1 reduziert.
              </p>
            </div>

            {/* iter88.9 Batch 5-fix: STX Add-On-Offers als kostenpflichtige Extras */}
            {area === "STILLTOUCHEDX" && stxAddonsList.length > 0 && (
              <div className="pt-4 border-t border-[#E5E5E5]">
                <label className="text-xs font-medium text-[#666] uppercase tracking-wider">
                  Add-Ons als kostenpflichtige Extras (optional)
                </label>
                <p className="text-[10px] text-[#999] mt-1 mb-3">
                  Wähle Zusatz-Optionen aus, die Kunden beim Kauf des Artikels dazu buchen können. Der Aufpreis pro Option wird zum Artikelpreis addiert. Extras sind nur in Kombination mit dem Artikel verfügbar.
                </p>
                <div className="space-y-1.5">
                  {stxAddonsList.map((a) => {
                    const active = stxAddonOffers.find((o) => o.code === a.code);
                    return (
                      <div key={a.code}>
                        <div data-testid={`editor-stx-addon-row-${a.code}`}
                          className={`flex items-center gap-2 px-3 py-2 border ${active ? "border-[#0A0A0A] bg-[#FAFAFA]" : "border-[#E5E5E5] bg-white"}`}>
                          <input type="checkbox"
                            data-testid={`editor-stx-addon-check-${a.code}`}
                            checked={!!active}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setStxAddonOffers((prev) => [...prev, { code: a.code, price: 0 }]);
                              } else {
                                setStxAddonOffers((prev) => prev.filter((o) => o.code !== a.code));
                              }
                            }}
                            className="w-4 h-4 accent-[#0A0A0A]" />
                          <span className="flex-1 text-xs font-medium text-[#0A0A0A]">{a.label}</span>
                          <input type="number" min="0" step="0.01"
                            data-testid={`editor-stx-addon-price-${a.code}`}
                            value={active?.price ?? ""}
                            disabled={!active}
                            onChange={(e) => {
                              const v = parseFloat(e.target.value) || 0;
                              setStxAddonOffers((prev) => prev.map((o) => o.code === a.code ? { ...o, price: v } : o));
                            }}
                            placeholder="0,00"
                            className="w-20 rounded-full h-8 px-2 border border-[#E5E5E5] text-xs text-right disabled:bg-[#F5F5F5] disabled:text-[#CCC]" />
                          <span className="text-[10px] text-[#999]">EUR</span>
                        </div>
                        {active && (
                          <div className="pl-6 -mt-1">
                            <PriceHint
                              endpoint="addon"
                              params={{ area: "STILLTOUCHEDX", code: a.code }}
                              onApply={(m) => setStxAddonOffers((prev) => prev.map((o) => o.code === a.code ? { ...o, price: m } : o))}
                              testid={`editor-stx-addon-hint-${a.code}`}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-[#999] mt-2">
                  {stxAddonOffers.length} von {stxAddonsList.length} als kostenpflichtige Extras angeboten.
                </p>
              </div>
            )}

            {/* iter88.9: Extra-Tragetage 1..7 als kostenpflichtige Zusatzoption */}
            {area === "STILLTOUCHEDX" && (
              <div className="pt-4 border-t border-[#E5E5E5]">
                <label className="text-xs font-medium text-[#666] uppercase tracking-wider">
                  Zusätzliche Tragetage (optional)
                </label>
                <p className="text-[10px] text-[#999] mt-1 mb-3">
                  Definiere Preise für längere Tragezeit. Kunde kann beim Kauf zwischen deinen freigeschalteten Tages-Optionen wählen (1-7 Tage extra).
                </p>
                <div className="space-y-1.5">
                  {[1, 2, 3, 4, 5, 6, 7].map((d) => {
                    const active = stxExtraWearOffers.find((o) => o.days === d);
                    return (
                      <div key={d}>
                        <div data-testid={`editor-stx-weardays-row-${d}`}
                          className={`flex items-center gap-2 px-3 py-2 border ${active ? "border-[#0A0A0A] bg-[#FAFAFA]" : "border-[#E5E5E5] bg-white"}`}>
                          <input type="checkbox"
                            data-testid={`editor-stx-weardays-check-${d}`}
                            checked={!!active}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setStxExtraWearOffers((prev) => [...prev, { days: d, price: 0 }].sort((a, b) => a.days - b.days));
                              } else {
                                setStxExtraWearOffers((prev) => prev.filter((o) => o.days !== d));
                              }
                            }}
                            className="w-4 h-4 accent-[#0A0A0A]" />
                          <span className="flex-1 text-xs font-medium text-[#0A0A0A]">+{d} Tag{d === 1 ? "" : "e"} zusätzlich</span>
                          <input type="number" min="0" step="0.01"
                            data-testid={`editor-stx-weardays-price-${d}`}
                            value={active?.price ?? ""}
                            disabled={!active}
                            onChange={(e) => {
                              const v = parseFloat(e.target.value) || 0;
                              setStxExtraWearOffers((prev) => prev.map((o) => o.days === d ? { ...o, price: v } : o));
                            }}
                            placeholder="0,00"
                            className="w-20 rounded-full h-8 px-2 border border-[#E5E5E5] text-xs text-right disabled:bg-[#F5F5F5] disabled:text-[#CCC]" />
                          <span className="text-[10px] text-[#999]">EUR</span>
                        </div>
                        {active && (
                          <div className="pl-6 -mt-1">
                            <PriceHint
                              endpoint="wear-days"
                              params={{ area: "STILLTOUCHEDX", days: d }}
                              onApply={(m) => setStxExtraWearOffers((prev) => prev.map((o) => o.days === d ? { ...o, price: m } : o))}
                              testid={`editor-stx-weardays-hint-${d}`}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 4: Media (multi-image, IMG-1) */}
        {step === 4 && (
          <div className="space-y-4" data-testid="editor-media-step">
            <p className="text-xs text-[#999]">
              Lade bis zu 6 Artikel-Bilder hoch{area === "STILLTOUCHEDX" ? " und bis zu 3 Trage-Fotos" : ""}.
              Wähle mit dem &#9733; dein Titelbild — das kann auch ein Trage-Foto sein.
            </p>
            {area !== "STILLTOUCHEDX" && <AnonymityNotice location="mp_article" />}

            {/* Existing media grid — Artikel-Fotos */}
            {(mediaUrls.length > 0) && (
              <div>
                <p className="text-xs font-medium text-[#666] uppercase tracking-wider mb-1.5">{t("listing_editor_extra.item_images")}</p>
                <div className="grid grid-cols-3 gap-2" data-testid="editor-media-grid">
                {mediaUrls.map((url, idx) => {
                  const effectiveTitle = titlePhotoUrl || mediaUrls[0];
                  const isTitle = effectiveTitle === url;
                  return (
                  <div key={url + idx} data-testid={`editor-media-thumb-${idx}`} className={`relative aspect-square bg-[#F5F5F5] border ${isTitle ? "border-[#0A0A0A]" : "border-[#E5E5E5]"} overflow-hidden group`}>
                    <img src={url} alt={`Bild ${idx + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                    {isTitle && (
                      <span className="absolute top-1 left-1 text-[9px] bg-[#0A0A0A] text-white px-1.5 py-0.5 uppercase tracking-wider">Titel</span>
                    )}
                    <div className="absolute top-1 right-1 flex gap-1">
                      {!isTitle && (
                        <button type="button" title="Als Titelbild" data-testid={`editor-media-cover-${idx}`}
                          onClick={() => setTitlePhotoUrl(url)}
                          className="w-6 h-6 bg-white/90 border border-[#E5E5E5] text-[10px] font-bold text-[#0A0A0A] hover:bg-white">★</button>
                      )}
                      <button type="button" title="Entfernen" data-testid={`editor-media-remove-${idx}`}
                        onClick={() => {
                          setMediaUrls((prev) => prev.filter((_, i) => i !== idx));
                          setBodyContentUrls((prev) => prev.filter((u) => u !== url));
                          if (titlePhotoUrl === url) setTitlePhotoUrl(null);
                          // iter172b · Fehler-Banner clearen wenn keine Medien mehr da sind
                          if (mediaUrls.length <= 1 && wornPhotoUrls.length === 0) setError("");
                        }}
                        className="w-6 h-6 bg-white/90 border border-[#E5E5E5] text-xs font-bold text-red-600 hover:bg-red-50">×</button>
                    </div>
                  </div>
                  );
                })}
                </div>
              </div>
            )}

            {/* Upload progress bars */}
            {uploadProgress.length > 0 && (
              <div className="space-y-1.5" data-testid="editor-upload-progress">
                {uploadProgress.map((up) => (
                  <div key={up.id} className="border border-[#E5E5E5] p-2 bg-[#FAFAFA]">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] text-[#666] truncate max-w-[70%]" title={up.fileName}>{up.fileName}</span>
                      <span className={`text-[11px] font-mono ${up.error ? "text-red-600" : "text-[#0A0A0A]"}`}>
                        {up.error ? "Fehler" : `${up.percent}%`}
                      </span>
                    </div>
                    <div className="h-1.5 bg-[#E5E5E5] overflow-hidden">
                      <div className={`h-full transition-all ${up.error ? "bg-red-500" : "bg-[#0A0A0A]"}`} style={{ width: `${up.percent}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* File picker (only if room left) */}
            {mediaUrls.length < 6 && (
              <div>
                <label className="text-xs font-medium text-[#666] uppercase tracking-wider">
                  Bild hochladen ({mediaUrls.length}/6)
                </label>
                {(() => {
                  // iter169.12 · Datei-Auswahl → Crop-Queue statt direkt uploaden.
                  const handleFiles = (e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length === 0) return;
                    setError("");
                    const slotsLeft = 6 - mediaUrls.length;
                    const toCrop = files.slice(0, slotsLeft);
                    if (files.length > slotsLeft) {
                      setError(`Nur ${slotsLeft} Bild(er) wurden hinzugefügt – Maximum sind 6.`);
                    }
                    e.target.value = "";
                    if (toCrop.length === 0) return;
                    setLastPickedFile(toCrop[0]);
                    setActiveCrop(toCrop[0]);
                    setCropQueue(toCrop.slice(1));
                  };
                  return (
                    <div className="mt-1">
                      <label className="h-12 w-full flex items-center justify-center gap-2 text-xs border border-[#E5E5E5] bg-white text-[#0A0A0A] cursor-pointer hover:bg-[#F5F5F5]">
                        Bild hinzufügen
                        <input data-testid="editor-media-upload" type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFiles} />
                      </label>
                    </div>
                  );
                })()}
                {lastPickedFile && (
                  <MediaAnalyseChip
                    file={lastPickedFile}
                    showFaceBlur={false}
                    testIdSuffix="listing-editor"
                  />
                )}
              </div>
            )}

            <div className="text-center text-xs text-[#CCC] py-1">{mediaUrls.length === 0 && wornPhotoUrls.length === 0 ? "Mindestens 1 Bild erforderlich (Artikel oder Trag-Foto)" : ""}</div>

            {area === "STILLTOUCHEDX" && (
              <div className="mt-6 pt-4 border-t border-dashed border-[#E5E5E5]">
                <WornPhotoUpload
                  listingId={draftListingId}
                  disabled={creatingDraft}
                  titleUrl={titlePhotoUrl}
                  onSetTitle={setTitlePhotoUrl}
                  onEnsureDraft={async () => {
                    if (draftListingId) return draftListingId;
                    setCreatingDraft(true);
                    setError("");
                    try {
                      const { data } = await api.post("/listings", {
                        area_scope: area,
                        title: title || "Entwurf",
                        description: description || "(Entwurf – wird beim Publish überschrieben)",
                        category_code: category || "sportswear",
                        condition_code: condition || "GOOD",
                        sale_mode: saleMode,
                        price: parseFloat(price) || 1.0,
                        segment,
                        media_urls: mediaUrls.slice(0, 6),
                        shipping_mode: shippingMode,
                        quantity: 1,
                        processing_delay_days: 0,
                        listing_status: "DRAFT",
                      });
                      setDraftListingId(data.listing_id);
                      return data.listing_id;
                    } catch (e) {
                      setError(formatError(e));
                      return null;
                    } finally {
                      setCreatingDraft(false);
                    }
                  }}
                  onChange={setWornPhotoUrls}
                />
              </div>
            )}
          </div>
        )}

        {/* Step 5: Shipping */}
        {step === 5 && (
          <div className="space-y-4">
            <p className="text-xs text-[#999] mb-2">{t("listing_editor_extra.choose_ship_method")}</p>
            <button onClick={() => setShippingMode("DIRECT_TO_BUYER")} data-testid="editor-ship-direct"
              className={`w-full p-4 border text-left transition-colors ${shippingMode === "DIRECT_TO_BUYER" ? "border-[#0A0A0A] bg-[#FAFAFA]" : "border-[#E5E5E5]"}`}>
              <p className="text-sm font-medium text-[#0A0A0A]">Direktversand</p>
              <p className="text-xs text-[#999] mt-1">{t("listing_editor_extra.you_ship")}</p>
            </button>
            <button onClick={() => setShippingMode("ANONYMOUS_HUB")} data-testid="editor-ship-anon"
              className={`w-full p-4 border text-left transition-colors ${shippingMode === "ANONYMOUS_HUB" ? "border-[#0A0A0A] bg-[#FAFAFA]" : "border-[#E5E5E5]"}`}>
              <p className="text-sm font-medium text-[#0A0A0A]">{t("listing_editor_extra.anon_ship")}</p>
              <p className="text-xs text-[#999] mt-1">{t("listing_editor_extra.anon_ship_desc")}</p>
            </button>

            {/* Sub-Batch SHIP-1: Direktversand-Konfiguration (Pflicht ab Spec §29/§36) */}
            {shippingMode === "DIRECT_TO_BUYER" && (
              <div className="pt-3 border-t border-[#E5E5E5] space-y-3" data-testid="editor-direct-ship-block">
                <p className="text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider">Direktversand – Details</p>
                {packageDefaultsFor(area, category) && (
                  <p className="text-[11px] text-[#666] bg-blue-50 border border-blue-100 px-2 py-1.5" data-testid="editor-pkg-suggested-hint">
                    Maße & Gewicht wurden basierend auf deiner Kategorie vorgeschlagen – bitte prüfen und ggf. anpassen.
                  </p>
                )}

                <div>
                  <label className="text-[10px] text-[#999] uppercase tracking-wider block mb-1">Versanddienstleister</label>
                  <select
                    data-testid="editor-carrier"
                    value={carrierCode}
                    onChange={(e) => setCarrierCode(e.target.value)}
                    className="w-full h-12 px-3 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A] bg-white">
                    <option value="DHL">DHL</option>
                    <option value="DEUTSCHE_POST">Deutsche Post</option>
                    <option value="HERMES">Hermes</option>
                    <option value="DPD">DPD</option>
                    <option value="UPS">UPS</option>
                  </select>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-[10px] text-[#999] uppercase tracking-wider block mb-1">{t("listing_editor_extra.length_cm")}</label>
                    <input data-testid="editor-pkg-length" type="number" min="1" step="0.1" value={pkgLength}
                      onChange={(e) => setPkgLength(e.target.value)}
                      className="w-full h-11 px-3 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" placeholder="30" />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#999] uppercase tracking-wider block mb-1">Breite (cm)</label>
                    <input data-testid="editor-pkg-width" type="number" min="1" step="0.1" value={pkgWidth}
                      onChange={(e) => setPkgWidth(e.target.value)}
                      className="w-full h-11 px-3 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" placeholder="20" />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#999] uppercase tracking-wider block mb-1">{t("listing_editor_extra.height_cm")}</label>
                    <input data-testid="editor-pkg-height" type="number" min="1" step="0.1" value={pkgHeight}
                      onChange={(e) => setPkgHeight(e.target.value)}
                      className="w-full h-11 px-3 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" placeholder="10" />
                  </div>
                </div>

                <div>
                  <label className="text-[10px] text-[#999] uppercase tracking-wider block mb-1">Gewicht (g)</label>
                  <input data-testid="editor-pkg-weight" type="number" min="1" step="1" value={pkgWeightG}
                    onChange={(e) => setPkgWeightG(e.target.value)}
                    className="w-full h-11 px-3 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" placeholder="500" />
                </div>

                <div>
                  <label className="text-[10px] text-[#999] uppercase tracking-wider block mb-1">Versandkosten</label>
                  <div className="grid grid-cols-2 gap-2">
                    <PillChip testId="editor-cost-custom"
                      active={shippingCostMode === "CUSTOM_AMOUNT"}
                      onClick={() => setShippingCostMode("CUSTOM_AMOUNT")}
                      variant="neutral" size="md" className="h-11 !justify-center">
                      Eigener Betrag
                    </PillChip>
                    <PillChip testId="editor-cost-live"
                      active={shippingCostMode === "LIVE_RATE"}
                      onClick={() => setShippingCostMode("LIVE_RATE")}
                      variant="neutral" size="md" className="h-11 !justify-center">
                      Live-Tarif
                    </PillChip>
                  </div>
                </div>

                {shippingCostMode === "CUSTOM_AMOUNT" && (
                  <div>
                    <label className="text-[10px] text-[#999] uppercase tracking-wider block mb-1">Versandkosten (EUR)</label>
                    <input data-testid="editor-cost-amount" type="number" min="0.01" step="0.01" value={customShippingAmount}
                      onChange={(e) => setCustomShippingAmount(e.target.value)}
                      className="w-full h-11 px-3 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" placeholder="4.99" />
                  </div>
                )}
              </div>
            )}

            {/* Sub-Batch Q-1b: Verzögerung Bearbeitung (per-article) */}
            <div className="pt-2">
              <label className="text-xs font-medium text-[#666] uppercase tracking-wider">{t("listing_editor_extra.processing_delay")}</label>
              <input
                data-testid="editor-processing-delay"
                type="number"
                min="0"
                max="60"
                step="1"
                value={processingDelay}
                onChange={(e) => setProcessingDelay(e.target.value)}
                className="mt-1 w-full h-12 px-4 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
                placeholder="0"
              />
              <p className="mt-1 text-[11px] text-[#999] leading-relaxed">
                Brauchst du für diesen Artikel länger als üblich (z.&nbsp;B. Vorbereitung, Reinigung)? Die Tage werden in
                der Artikelanzeige als <strong>{t("listing_editor_extra.extra_time")}</strong> ausgewiesen und kommen zur Standard-Lieferzeit
                deines Bereichs hinzu. Bei STX-Artikeln wird sie später optional auch zur Tragedauer addiert.
              </p>
            </div>

            {/* Sub-Batch TPL-1: Als Vorlage speichern */}
            {!isEditMode && (
              <div className="pt-3 border-t border-[#E5E5E5]">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="editor-save-as-template"
                    checked={saveAsTemplate}
                    onChange={(e) => {
                      setSaveAsTemplate(e.target.checked);
                      if (e.target.checked && !templateName) {
                        setTemplateName(title || `Vorlage ${new Date().toLocaleDateString("de-DE")}`);
                      }
                    }}
                    className="mt-1 w-4 h-4 accent-[#0A0A0A]"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#0A0A0A]">{t("listing_editor_extra.save_as_template")}</p>
                    <p className="text-[11px] text-[#999] mt-0.5">
                      Spart die wichtigsten Eingaben als wiederverwendbare Vorlage (z.&nbsp;B. wenn du regelmäßig ähnliche Artikel einstellst).
                      Vorlagen findest du beim nächsten Mal in Schritt&nbsp;1/6.
                    </p>
                  </div>
                </label>
                {saveAsTemplate && (
                  <div className="mt-3 pl-7">
                    <label className="text-[10px] text-[#999] uppercase tracking-wider block mb-1">Name der Vorlage</label>
                    <input
                      data-testid="editor-template-name"
                      type="text"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      maxLength={100}
                      placeholder="z.B. Damen-Bluse Standard"
                      className="w-full h-10 px-3 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom CTA */}
      <div className="sticky bottom-16 bg-white border-t border-[#E5E5E5] px-4 py-3 flex gap-2">
        {step > 0 && (
          <button onClick={() => setStep(step - 1)} className="h-11 px-6 border border-[#E5E5E5] text-sm font-medium text-[#666] transition-colors hover:bg-[#F5F5F5] active:scale-[0.98]">
            Zurück
          </button>
        )}
        {step < STEPS.length - 1 ? (
          <button data-testid="editor-next" onClick={() => canProceed() && !stxWizardBlocked && setStep(step + 1)} disabled={!canProceed() || stxWizardBlocked}
            className="flex-1 rounded-full h-11 bg-[#0A0A0A] text-white text-sm font-medium flex items-center justify-center gap-1.5 transition-colors hover:bg-[#222] active:scale-[0.98] disabled:opacity-30">
            Weiter <ArrowRight size={14} />
          </button>
        ) : (
          <button data-testid="editor-publish" onClick={handlePublish} disabled={publishing || !canProceed() || stxWizardBlocked}
            className="flex-1 rounded-full h-11 bg-[#0A0A0A] text-white text-sm font-medium flex items-center justify-center gap-1.5 transition-colors hover:bg-[#222] active:scale-[0.98] disabled:opacity-30">
            {publishing
              ? (isEditMode ? "Wird gespeichert..." : "Wird veröffentlicht...")
              : (isEditMode ? "Speichern" : "Veröffentlichen")}
          </button>
        )}
      </div>

      {/* Sub-Batch TPL-1: Templates Modal */}
      {showTemplatesModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" onClick={() => setShowTemplatesModal(false)}>
          <div data-testid="templates-modal" className="w-full sm:max-w-md max-h-[80vh] overflow-y-auto bg-white border border-[#E5E5E5] shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5] sticky top-0 bg-white">
              <h3 className="text-sm font-bold text-[#0A0A0A]">Vorlagen ({area === "STILLTOUCHEDX" ? "stilltouchedX" : "Marketplace"})</h3>
              <button onClick={() => setShowTemplatesModal(false)} className="text-[#999] text-xl leading-none">×</button>
            </div>
            <div className="divide-y divide-[#E5E5E5]">
              {templates.length === 0 ? (
                <p className="px-4 py-6 text-sm text-[#999] text-center">{t("listing_editor_extra.no_templates")}</p>
              ) : (
                templates.map((tpl) => (
                  <div key={tpl.template_id} data-testid={`template-row-${tpl.template_id}`} className="px-4 py-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#0A0A0A] truncate">{tpl.name}</p>
                      <p className="text-[10px] text-[#999] mt-0.5 truncate">
                        {tpl.title || "—"}{tpl.price ? ` · ${tpl.price?.toFixed(2)} €` : ""}{tpl.category_code ? ` · ${tpl.category_code}` : ""}
                      </p>
                    </div>
                    <button
                      data-testid={`template-apply-${tpl.template_id}`}
                      onClick={() => applyTemplate(tpl)}
                      className="h-8 px-3 bg-[#0A0A0A] text-white text-xs hover:bg-[#222]"
                    >
                      Verwenden
                    </button>
                    <button
                      data-testid={`template-delete-${tpl.template_id}`}
                      onClick={() => deleteTemplate(tpl.template_id)}
                      className="h-8 px-2 border border-red-300 text-red-600 text-xs hover:bg-red-50"
                      title={t("listing_editor_extra.delete_template")}
                    >
                      ×
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
      {/* iter169.12 · Bild-Crop-Dialog (4:5 identisch zu Marketplace/STX-Detail-Aspect) */}
      {activeCrop && (
        <ImageCropDialog
          file={activeCrop}
          aspect={4 / 5}
          title={`Bild zuschneiden (${(cropQueue.length ? cropQueue.length + 1 : 1)} von ${(cropQueue.length + 1)}) · 4:5`}
          onCancel={() => {
            const next = cropQueue[0] || null;
            setActiveCrop(next);
            setCropQueue(cropQueue.slice(1));
          }}
          onCropped={async (blob) => {
            // iter173e · Zuerst durch die Anonymisierungs-Engine, dann Upload.
            const named = new File([blob], blob.name || "listing.jpg", { type: blob.type || "image/jpeg" });
            setAnonArticleFile(named);
          }}
        />
      )}

      {/* iter173e · Anonymisierungs-Engine für STX-/MP-Artikelbilder (returnBlob:
          Explicit-Block bleibt im bestehenden /upload erhalten). */}
      {anonArticleFile && (
        <AnonymizeDialog
          file={anonArticleFile}
          context={area === "STILLTOUCHEDX" ? "STX_ARTICLE" : "MP_ARTICLE"}
          returnBlob
          onCancel={() => {
            setAnonArticleFile(null);
            const next = cropQueue[0] || null;
            setActiveCrop(next);
            setCropQueue(cropQueue.slice(1));
          }}
          onComplete={async (finalBlob) => {
            setAnonArticleFile(null);
            await uploadArticleBlob(finalBlob);
            const next = cropQueue[0] || null;
            setActiveCrop(next);
            setCropQueue(cropQueue.slice(1));
          }}
        />
      )}

      {/* iter173d · Ladeoverlay während Artikelbild-Upload + Neutralitäts-Prüfung
          (analog Trage-Foto-Wizard) — Seller sieht, dass das Bild verarbeitet wird. */}
      {articleUploadPhase && (
        <div data-testid="article-upload-overlay" className="fixed inset-0 z-[10000] bg-black/70 flex items-center justify-center pointer-events-none">
          <div className="bg-white rounded-2xl px-6 py-5 flex flex-col items-center gap-3 shadow-2xl max-w-xs">
            {articleUploadPhase === "analyzing" ? (
              <ScanSearch className="w-8 h-8 text-[#0A0A0A] animate-pulse" />
            ) : (
              <Loader2 className="w-8 h-8 text-[#0A0A0A] animate-spin" />
            )}
            <p className="text-sm font-semibold text-[#0A0A0A]">
              {articleUploadPhase === "analyzing" ? t("listing_editor_extra.article_analyzing_title") : t("listing_editor_extra.article_uploading_title")}
            </p>
            <p className="text-[11px] text-[#666] text-center leading-snug">
              {articleUploadPhase === "analyzing"
                ? t(area === "MARKETPLACE" ? "listing_editor_extra.article_analyzing_desc_mp" : "listing_editor_extra.article_analyzing_desc_stx")
                : t("listing_editor_extra.article_uploading_desc")}
            </p>
            <div className="w-full h-1.5 bg-[#E5E5E5] rounded-full overflow-hidden">
              <div className={`h-full bg-[#0A0A0A] ${articleUploadPhase === "analyzing" ? "w-2/3 animate-pulse" : "w-1/3 animate-pulse"}`} />
            </div>
          </div>
        </div>
      )}

      {/* iter171 · EU-AI-Act Art. 50 · Redaktionelle Freigabe des KI-Textes */}
      <AIReviewDialog
        open={!!aiReview}
        originalText={description}
        generated={aiReview?.text || ""}
        action={aiReview?.action || ""}
        onAccept={(text) => { setDescription(text); setAiReview(null); }}
        onReject={() => setAiReview(null)}
      />
    </div>
  );
}
